import { inject, Injectable } from '@angular/core';
import { COMMON_VALIDATION, EMAIL, INDIVIDUAL_NAME, NRIC } from '@core/data-access';
import { get18YearsAgo } from '@core/util';
import { PortalFieldConfig, uppercaseParser } from '@portal/form/data-access';
import { AboutYouModelKeys } from '../const/form-field-key.const';
import { AuthorizedPersonnelFormConfigService } from './authorized-personnel-form-config.service';
import { MobileFormConfigService } from './mobile-form-config.service';

@Injectable({ providedIn: 'root' })
export class AboutYouFormFieldConfigService {
  private readonly mobileFormConfigService = inject(MobileFormConfigService);
  private readonly authorizedPersonnelFormConfigService = inject(AuthorizedPersonnelFormConfigService);

  initAboutYouFieldConfig(): PortalFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'field-columns',
        fieldGroup: [
          {
            key: AboutYouModelKeys.name,
            type: 'input',
            defaultValue: '',
            props: {
              label: 'Name as per NRIC/Passport',
              placeholder: 'e.g. Roland Tey',
              disabled: true,
              maxLength: COMMON_VALIDATION.MAX_LENGTH,
              maskConfig: {
                mask: 'a*',
                config: { patterns: { a: { pattern: INDIVIDUAL_NAME.PATTERN } }, dropSpecialCharacters: false },
              },
            },
          },
          {
            key: AboutYouModelKeys.nricNumber,
            type: 'input',
            defaultValue: '',
            props: {
              label: 'NRIC/Passport No.',
              placeholder: 'e.g. S1234567D',
              minLength: NRIC.MIN_LENGTH,
              maxLength: NRIC.MAX_LENGTH,
              disabled: true,
              maskConfig: { mask: 'A*' },
            },
            // validators: { validation: ['nric'] },
            parsers: [uppercaseParser],
          },
          {
            key: AboutYouModelKeys.gender,
            type: '#gender',
            validators: { validation: ['gender'] },
            props: { required: true },
            expressions: { 'props.disabled': 'formState.isMyInfo' },
          },
          {
            key: AboutYouModelKeys.dateOfBirthOnly,
            type: 'datepicker',
            validators: { validation: ['dateRange'] },
            props: {
              label: 'Date of Birth',
              placeholder: 'Please select date',
              // minDate: getDate100YearsAgo(),
              // maxDate: getEndOfToday(),
              maxDate: get18YearsAgo(),
              required: true,
            },
            expressions: { 'props.disabled': 'formState.isMyInfo' },
          },
          {
            key: AboutYouModelKeys.placeOfBirthCountryId,
            type: '#country',
            props: { label: 'Country of Birth', required: true },
            expressions: { 'props.disabled': 'formState.isMyInfo' },
          },
          {
            key: AboutYouModelKeys.nationalityId,
            type: '#nationality',
            props: { required: true },
            expressions: { 'props.disabled': 'formState.isMyInfo' },
          },
          {
            key: AboutYouModelKeys.residentialStatus,
            type: '#residentialStatus',
            expressions: { 'props.disabled': 'formState.isMyInfo' },
          },
        ],
      },
      {
        template: '<div class="portal-paragraph">Help us update your information</div>',
      },
      {
        fieldGroupClassName: 'field-columns',
        fieldGroup: [
          {
            fieldGroup: this.mobileFormConfigService.initMobileFieldConfig({
              label: 'Mobile Number',
              key: { areaCode: AboutYouModelKeys.contactAreaCode, number: AboutYouModelKeys.contactNumber },
            }),
          },
          {
            key: AboutYouModelKeys.email,
            type: 'input',
            defaultValue: '',
            props: {
              label: 'Email Address',
              placeholder: 'eg. roland.tey@gmail.com',
              maxLength: EMAIL.MAX_LENGTH,
              required: true,
            },
            validators: {
              validation: [{ name: 'email-custom', options: { keyName: AboutYouModelKeys.email } }],
            },
            // parsers: [lowercaseParser],
          },
          {
            resetOnHide: false,
            className: 'col-start-1 sm:col-span-2',
            fieldGroup: this.authorizedPersonnelFormConfigService.initPersonnelAddressFormFields(),
          },
          {
            key: AboutYouModelKeys.maritalStatus,
            type: '#maritalStatus',
            props: { required: true },
          },
          {
            key: AboutYouModelKeys.dualNationality,
            type: '#yesNo',
            props: {
              label: 'Other Nationalities',
              required: true,
            },
          },
          {
            key: AboutYouModelKeys.dualNationalityId,
            type: '#nationality',
            props: { label: 'Other Nationalities (Please indicate)' },
            expressions: {
              hide: field => !field.model?.[AboutYouModelKeys.dualNationality],
              'props.required': ({ model }) => model?.[AboutYouModelKeys.dualNationality],
            },
          },
          // {
          //   key: AboutYouModelKeys.cpfAttachments,
          //   type: 'file',
          //   expressions: { hide: '!formState.isMyInfo' },
          //   className: 'col-start-1',
          //   props: {
          //     label: 'CPF Contribution History',
          //     acceptMimeTypes: Array.from<string>(ACCEPT_MIME_TYPES),
          //     disabled: true,
          //   },
          // },
          // {
          //   key: AboutYouModelKeys.noaAttachments,
          //   type: 'file',
          //   expressions: { hide: '!formState.isMyInfo' },
          //   className: 'col-start-1',
          //   props: {
          //     label: 'Notice of Assessment',
          //     acceptMimeTypes: Array.from<string>(ACCEPT_MIME_TYPES),
          //     disabled: true,
          //   },
          // },
        ],
      },
    ];
  }
}
